import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "../api/axiosConfig";
import {
    Box,
    Pagination,
    Skeleton,
    Button,
    Typography,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    TextField,
    InputAdornment,
    Tooltip,
    Paper,
    Divider,
} from "@mui/material";
import * as XLSX from "xlsx";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import RecordDetailModal from "../components/RecordDetailModal";
import DataHelper from "../utils/DataHelper";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export default function ReportsPage() {
    const { jenisReport } = useParams();

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 100;

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const handleOpenDetail = (row) => {
        setSelectedRecord(row);
        setModalOpen(true);
    };

    const [judulLaporan, setJudulLaporan] = useState(jenisReport || "Laporan");
    const [subjudulLaporan, setSubJudulLaporan] = useState("");
    const [kolomStyle, setKolomStyle] = useState([]);
    const [judulkolom, setJudulKolom] = useState([]);
    const [fieldToSum, setFieldToSum] = useState("amount");

    const [rows, setRows] = useState([]);

    const [selectedStartDate, setSelectedStartDate] = useState(dayjs().startOf("month"));
    const [selectedEndDate, setSelectedEndDate] = useState(dayjs().endOf("month"));

    const fetchData = async () => {
        try {
            setLoading(true);

            const resp = await axios.post("/reports.php", {
                reporttype: jenisReport,
                param: {
                    startdate: selectedStartDate ? selectedStartDate.format("YYYY-MM-DD") : "2024-01-01",
                    enddate: selectedEndDate ? selectedEndDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
                },
            });

            if (resp.data.success) {
                setRows(Array.isArray(resp.data.data) ? resp.data.data : []);
                setJudulKolom(resp.data.judulkolom || []);
                setKolomStyle(
                    (resp.data.judulkolom || []).map((title, index) => {
                        const meta = DataHelper.parseColumnStyle(resp.data.kolomstyle[index]);

                        return {
                            title,
                            ...meta,
                            hidden: meta.type === "H",
                        };
                    })
                );
                setFieldToSum(resp.data.fieldToSum || "amount");
                setJudulLaporan(resp.data.judulLaporan || jenisReport);
                setSubJudulLaporan(resp.data.subjudulLaporan || "");
            }
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRows([]);
        setSearch("");
        setPage(1);
        fetchData();
    }, [jenisReport]);

    const filteredRows = useMemo(() => {
        const safeRows = Array.isArray(rows) ? rows : [];

        if (!search) return safeRows;

        const term = search.toLowerCase();

        return safeRows.filter(
            (r) => r.name_full?.toLowerCase().includes(term) || r.idjemaat?.toLowerCase().includes(term)
        );
    }, [rows, search]);

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(wb, ws, jenisReport);
        XLSX.writeFile(wb, `${jenisReport}.xlsx`);
    };

    const totalSelected = rows.reduce((sum, item) => sum + Number(item[fieldToSum] || 0), 0);

    const paginatedRows = useMemo(() => {
        const safeRows = Array.isArray(filteredRows) ? filteredRows : [];
        const start = (page - 1) * rowsPerPage;
        return safeRows.slice(start, start + rowsPerPage);
    }, [filteredRows, page]);

    const visibleColumns = useMemo(() => kolomStyle.filter((c) => !c.hidden), [kolomStyle]);

    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper
                sx={{
                    p: 2.5,
                    borderRadius: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    background:
                        "linear-gradient(135deg, rgba(15, 23, 42, 0.03) 0%, rgba(59, 130, 246, 0.05) 100%)",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                            Reports
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                            Laporan {judulLaporan}
                        </Typography>
                        {subjudulLaporan && (
                            <Typography variant="body2" color="text.secondary">
                                {subjudulLaporan}
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Tooltip title="Export to Excel">
                            <Button
                                variant="contained"
                                color="success"
                                onClick={exportToExcel}
                                startIcon={<DownloadIcon />}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    px: 2.5,
                                }}
                            >
                                Export
                            </Button>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 3, 
                border: "1px solid", borderColor: "divider" }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
                            <DatePicker
                                label="Start Date"
                                value={selectedStartDate}
                                onChange={(newValue) => setSelectedStartDate(newValue)}
                                format="DD MMMM YYYY"
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { minWidth: 180 },
                                    },
                                }}
                            />

                            <DatePicker
                                label="End Date"
                                value={selectedEndDate}
                                onChange={(newValue) => setSelectedEndDate(newValue)}
                                format="DD MMMM YYYY"
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { minWidth: 180 },
                                    },
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => fetchData()}
                                startIcon={<SearchIcon />}
                                sx={{
                                    height: 40,
                                    borderRadius: 2,
                                    textTransform: "none",
                                    px: 2,
                                }}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </LocalizationProvider>

                    <TextField
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name or ID"
                        sx={{ minWidth: { xs: "100%", md: 260 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
            </Paper>

            {/* <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Paper
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        minWidth: 180,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Records
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {filteredRows.length.toLocaleString()}
                    </Typography>
                </Paper>

                <Paper
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        minWidth: 180,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {totalSelected.toLocaleString()}
                    </Typography>
                </Paper>
            </Stack> */}

            <Paper
                sx={{
                    borderRadius: 0,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                }}
            >
                <TableContainer sx={{ maxHeight: "71vh" }}>
                    {loading ? (
                        <Box sx={{ p: 2 }}>
                            {[...Array(12)].map((_, i) => (
                                <Skeleton key={i} variant="rectangular" height={40} sx={{ my: 0.6, borderRadius: 1 }} />
                            ))}
                        </Box>
                    ) : (
                        <Table stickyHeader size="small" aria-label="reports table">
                            <TableHead>
                                <TableRow>
                                    {visibleColumns.map((col, i) => (
                                        <TableCell
                                            key={i}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: 12,
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                backgroundColor: "background.paper",
                                                color: "text.secondary",
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                width: col.width ? `${col.width}px` : "auto",
                                                textAlign: col.type === "N" ? "right" : "left",
                                            }}
                                        >
                                            {col.title}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={visibleColumns.length} sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary" align="center">
                                                No data available for the selected range.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedRows.map((row, rowIndex) => (
                                        <TableRow
                                            key={rowIndex}
                                            hover
                                            // onClick={() => handleOpenDetail(row)}
                                            sx={{
                                                cursor: "pointer",
                                                transition: "background 0.15s",
                                                "&:hover": { backgroundColor: "action.hover" },
                                            }}
                                        >
                                            {judulkolom.map((col, colIndex) => {
                                                if (col.hidden) return null;

                                                const keys = Object.keys(row);
                                                const value = row[keys[colIndex]];

                                                return (
                                                    <TableCell
                                                        key={colIndex}
                                                        sx={{
                                                            fontSize: 13,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            width: col.width ? `${col.width}px` : "auto",
                                                            textAlign: col.type === "N" ? "right" : "left",
                                                        }}
                                                    >
                                                        {DataHelper.formatValue(col.type, value)}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Paper>

            {!loading && (
                <Paper
                    sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "background.paper",
                    }}
                >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" justifyContent="space-between">
                        <Pagination
                            count={Math.ceil(filteredRows.length / rowsPerPage)}
                            page={page}
                            siblingCount={0}
                            boundaryCount={1}
                            onChange={(e, v) => setPage(v)}
                            shape="rounded"
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    borderRadius: "10px",
                                    fontSize: "13px",
                                    minWidth: "34px",
                                    height: "34px",
                                },
                                "& .Mui-selected": {
                                    backgroundColor: "#111827 !important",
                                    color: "#fff",
                                },
                            }}
                        />

                        <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                            <Box textAlign="center">
                                <Typography fontSize={14} fontWeight={700}>
                                    {filteredRows.length.toLocaleString()}
                                </Typography>
                                <Typography fontSize={10} color="text.secondary">
                                    Records
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography fontSize={14} fontWeight={700}>
                                    {totalSelected.toLocaleString()}
                                </Typography>
                                <Typography fontSize={10} color="text.secondary">
                                    Total
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            <RecordDetailModal open={modalOpen} onClose={() => setModalOpen(false)} data={selectedRecord} />
        </Box>
    );
}
